import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router";
import SearchForm from "../../stories/Forms/SearchForm";
import {
  BarcodeOneTubeFields,
  BarcodeTwoTubeFields,
  ClinicalBarcodeFields,
  VhBarcodeOneTubeFields,
  VhBarcodeTwoTubeFields,
  VhClinicalBarcodeFields,
  links,
  vhKitRejectionReasons,
  vhAppointmentRejectionReasons,
} from "../../stories/Forms/fields";
import useApis from "../../services/useAPI";
import { useAuth } from "../../components/AuthContext/AuthContext";
import { postUrlBarcode } from "../../utils/constants";
import {
  enterBarcodeSchema,
  vhClinicalBarcodeSchema,
} from "../../stories/Forms/validationSchema";
import logo from "../../assets/images/boots-myhealthchecked-logo.svg";
import img from "../../assets/images/notification.svg";
import LoggedInLayout from "../../stories/LoggedInScreens/LoggedInLayout";
import moment from "moment";
import {
  formatTestType,
  TimeFormatter,
  formatBarcode,
  formatStudyName,
  formatDtAge,
  formatDate,
} from "../../utils/utils";
import { ArrowRightCircleFill, XCircleFill, ArrowRight, XLg } from "react-bootstrap-icons";
import Alert from "react-bootstrap/Alert";
import PageLayout from "../../components/Layout/PageLayout";
import Notes from "../../stories/Forms/Notes";
import Spinner from "react-bootstrap/Spinner";
import RejectKitModal from "../../stories/Modals/RejectKitModal";
import ExitModal from "../../stories/Modals/ExitModal";
import VHConfirmationPopup from "../../stories/Modals/VHConfirmationPopup";
import ExitConsultationButton from "../../stories/Buttons/ExitConsultationButton";
import ConfirmModal from "../../stories/Modals/ConfirmModal";
import infoIcon from "../../assets/icons/Information-fill.svg";

const confirmContent = {
  kit: {
    title: "Confirm test kit rejection?",
    text: "Confirm that you are rejecting the test kit with barcode:",
  },
  exit: {
    title: "Confirm rejection",
    text: "Are you sure you want to end this consultation?",
  },
};

function EnterBarcode() {
  // Get ID from URL and initialize navigation
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); 
  const formikRef = useRef(null);
  const [notes, setNotes] = useState("");
  const [showKitModal, setShowKitModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitNotes, setExitNotes] = useState("");
  const [pendingReject, setPendingReject] = useState(null);
  const [kitRejected, setKitRejected] = useState(false);

  // State variables for managing form data and UI state
  const [formData, setFormData] = useState({});
  const [secondaryBarcode, setSecondaryBarcode] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [userData, setUserData] = useState(null);
  const [isRejectDisabled, setIsRejectDisabled] = useState(true);
  const [updateKey, setUpdateKey] = useState(0);
  const [data, setData] = useState([]);
  const [isRejectButtonDisplay, setIsRejectButtonDisplay] = useState(true);
  const auth = useAuth();
  // API endpoints for GET and POST requests
  const getUrl = `/api/phlebotomists-portal/test-confirmation/${id}/`;
  const clinicalurl = `/api/phlebotomists-portal/clinical-test-confirmation/${id}/`;
  const postUrl = `/api/phlebotomists-portal/selected-test-type/${id}/`;
  const putUrlReject = "/api/phlebotomists-portal/kit-rejection";
  const repeatTestUrl = `/api/phlebotomists-portal/register-appointment/${id}/`;

  // Custom hook to handle API calls and responses
  const {
    callApi,
    resultGet,
    resultPost,
    isLoading,
    serverError,
    showError,
    tokenError,
    toggleShowError,
  } = useApis();

  const { callApi: callRepeatTestApi, resultGet: repeatTestResult } = useApis();

  useEffect(() => {
    if (searchParams.get("reload") === "true") {
      
      localStorage.removeItem("secondary_barcode");
      localStorage.removeItem("sample_collection_date");
      localStorage.removeItem("sample_collection_time");
      localStorage.removeItem("number_of_attempt");
      
      setFormData({});
      setSecondaryBarcode("");
      setExpiryDate("");
      setData([]);
      setNotes("");
      setIsRejectDisabled(true);
      setIsRejectButtonDisplay(true);
      setUpdateKey((prev) => prev + 1);
    }
  }, [searchParams]);

  const stages = ["patient-details","confirm-details","enter-barcode","appointment-confirmation"];
  
  useEffect(() => {
    if (!stages.includes(sessionStorage.getItem("allowedStage")) && auth?.featureFlag) {
      navigate("/patient-record");
    }
  },[sessionStorage.getItem("allowedStage"), auth?.featureFlag, navigate]);

  useEffect(() => {
    if (serverError || tokenError) {
      auth?.showToast(
        "Error",
        tokenError || serverError,
        "../assets/mhc.png",
        {icon: <XCircleFill />,
          className: auth?.featureFlag && "vh-toaster"
        },
      );
    }
  }, [serverError, tokenError]);

  // Access authentication data from context
  let selectedService = localStorage.getItem("selectedService");

  // Submit form data via POST request
  const handleSubmit = async (formData) => {
    const formattedTestType = formatTestType(resultGet.booking_info.test_type);
    setFormData((prevFormData) => ({
      ...prevFormData,
      test_type: formattedTestType,
    }));
    setData((prevData) => {
      return { ...prevData, ...formData }; // Adjust based on your update logic
    });
    formData.study = formatStudyName("Thrive Registry");

    // Wait for the meet_details to be updated before proceeding
    if (formData?.sample_collection_date && formData?.sample_collection_time) {
      const formattedDateTime = await TimeFormatter(
        formData.sample_collection_date,
        formData.sample_collection_time,
      );

      // Update formData with the formatted datetime
      formData.sample_collection_time = formattedDateTime;
    }

    if (formData?.number_of_attempt !== undefined) {
      formData.number_of_attempt = Number.parseInt(
        formData.number_of_attempt,
        10,
      );
    }
    if (notes) {
      formData.notes = notes;
    }
    // Call the API with updated formData
    if (formData?.sample_collection_date && formData?.sample_collection_time) {
      await callApi(formData, postUrl, "POST", {
        Authorization: `Bearer ${
          localStorage.getItem("access_token") || auth.accessToken
        }`,
        "Content-Type": "application/json",
      });
    }
  };

  // Fetch initial data on mount
  useEffect(() => {
    let url = getUrl;
    auth.setTitle("PATIENT RECORD");
    if (selectedService === "clinical") {
      url = clinicalurl;
      auth.setTitle("Clinical studies");
    }
    const fetchData = async () => {
      await callApi("", url, "GET", {
        Authorization: `Bearer ${
          localStorage.getItem("access_token") || auth.accessToken
        }`,
        "Content-Type": "application/json",
      });
    };

    fetchData();
  }, [id,searchParams]);

  useEffect(() => {
    if (auth?.featureFlag && selectedService === "healthCare") {
      callRepeatTestApi(null, repeatTestUrl, "GET");
    }
  }, [id, auth?.featureFlag]);

  useEffect(() => {
    if (searchParams.get("reload") === "true" && resultGet) {
      navigate(`/enter-barcode/${id}`, { replace: true });
    }
  }, [resultGet, searchParams, id, navigate]);

  // Update state based on API responses
  useEffect(() => {
    if (resultGet) {
      if (selectedService === "healthCare") {
        auth.setTitle(
          `In-store Wellness Testing Service - ${resultGet?.booking_info?.test_type}`,
        );
      }
      setUserData(resultGet.booking_info.test_type);
      localStorage.setItem("booking_id", resultGet?.booking_info?.booking_id);
    }
    if (resultPost) {
      setIsRejectButtonDisplay(true);
      setFormData((prevFormData) => ({
        ...prevFormData,
        primary_barcode: resultPost.primary_barcode,
        secondary_barcode: formatBarcode(resultPost.secondary_barcode),
        expiry_date: resultPost.expiry_date,
        study: formatStudyName("Thrive Registry"),
      }));
      setSecondaryBarcode(resultPost.secondary_barcode);
      setExpiryDate(resultPost.expiry_date);
      setIsRejectDisabled(!resultPost.primary_barcode);
    } else {
      toggleShowError(); // Show error toaster on barcode validation failure
    }
  }, [resultGet, resultPost]);

  // Handle server errors
  useEffect(() => {
    if (serverError) {
      if (
        serverError.includes("Please enter valid primary barcode") ||
        serverError.includes("Primary barcode does not exist") ||
        serverError.includes("Entered primary barcode is already used") ||
        serverError.includes("Primary barcode has expired") ||
        serverError.includes("This test kit barcode has been rejected and cannot be reused. Please assign a new test kit.") ||
        serverError.includes("Invalid barcode scanned. Please check the barcode and try again.")
      ) {
        // Reset form fields if barcode errors occur
        setFormData({
          primary_barcode: "",
          secondary_barcode: "",
          expiry_date: "",
        });
        setSecondaryBarcode("");
        setExpiryDate("");
        setIsRejectDisabled(true);
        setUpdateKey((prev) => prev + 1); // Trigger re-render
      }
    }
  }, [serverError]);

  const fields = useMemo(() => {
    let f;
    if (selectedService === "clinical") {
      f = ClinicalBarcodeFields;
    } else if (resultGet?.booking_info?.vessel_info_2) {
      f = BarcodeTwoTubeFields;
    } else {
      f = BarcodeOneTubeFields;
    }

    if (auth?.featureFlag) {
      if (selectedService === "clinical") {
        f = VhClinicalBarcodeFields;
      } else if (resultGet?.booking_info?.vessel_info_2) {
        f = VhBarcodeTwoTubeFields;
      } else {
        f = VhBarcodeOneTubeFields;
      }
    }
    return f;
  }, [selectedService, resultGet, auth?.featureFlag]);

  // Handle successful submission
  useEffect(() => {
    const isVhClinical = auth?.featureFlag && selectedService === "clinical";

    const redirect = auth?.featureFlag
      ? selectedService === "clinical"
        ? `/clinical-confirmation-steps/${id}`
        : `/confirmation-steps/${id}`
      : `/appointment-confirmation/${id}`;

    if (resultPost?.selected_test_type === true) {
      if (isVhClinical) auth?.allowStage("enter-barcode");
      auth?.showToast(
        "Success",
        `${resultPost.test_type} Test assigned to kit ${resultPost.primary_barcode}`,
        "../assets/mhc.png",
        {icon: <ArrowRightCircleFill />,
        className: auth?.featureFlag && "vh-toaster"},
      );
      navigate(redirect, {
        state: {
          study: resultPost?.study || formatStudyName("Thrive Registry"),
          secondary_barcode: secondaryBarcode,
          testType: resultPost?.test_type,
        },
        replace: isVhClinical,
      });
    } else {
      toggleShowError(); // Show error toaster on API call failure
    }
  }, [resultPost]);

  // Handle 'Reject' button click
  const handleRejectClick = () => {
    navigate(`/appointment-confirmation/${id}`, {
      state: {
        rejectPath: true,
        primary_barcode: resultPost?.primary_barcode || null,
        secondary_barcode: resultPost?.secondary_barcode || null,
        testkit: resultPost?.testkit || null, // Optionally pass it to the next page
      },
    });
  };

  const handleRejectKitClick = () => setShowKitModal(true);
  const handleExitConsultationClick = () => setShowExitModal(true);
  const closeExitModal = () => {
    setShowExitModal(false);
    setExitNotes("");
  };

  const formValues = formikRef.current?.values || {};

  const patientName =
    resultGet?.user_info?.full_name ||
    [resultGet?.user_info?.first_name, resultGet?.user_info?.last_name]
      .filter(Boolean)
      .join(" ") ||
    "";

  const dobDisplay = resultGet?.user_info?.dob
    ? formatDtAge(resultGet.user_info.dob, true)
    : "";

  const collectionDate =
    formValues.sample_collection_date ||
    localStorage.getItem("sample_collection_date") ||
    (resultGet?.booking_info?.consultation_ts
      ? formatDate(resultGet.booking_info.consultation_ts)
      : moment().format("DD/MM/YYYY"));

  const collectionTime =
    formValues.sample_collection_time ||
    localStorage.getItem("sample_collection_time") ||
    (resultGet?.booking_info?.consultation_ts
      ? moment(resultGet.booking_info.consultation_ts).format("HH:mm")
      : moment().format("HH:mm"));

  const numberOfAttempts =
    formValues.number_of_attempt ||
    localStorage.getItem("number_of_attempt") ||
    data?.number_of_attempt ||
    "";

  const enteredDate =
    formValues.sample_collection_date ||
    localStorage.getItem("sample_collection_date") ||
    "";

  const enteredTime =
    formValues.sample_collection_time ||
    localStorage.getItem("sample_collection_time") ||
    "";

  const displayCollectionDate = (() => {
    if (!enteredDate) return "";
    const parsed = moment(enteredDate, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : enteredDate;
  })();

  const isTwoTube = Boolean(resultGet?.booking_info?.vessel_info_2);

  const kitDetails = [
    ["Patient name:", patientName],
    ["Date of birth:", dobDisplay],
    ["Consultation ID:", resultGet?.booking_info?.booking_id],
    ["Test type:", userData || resultGet?.booking_info?.test_type || ""],
    ["SST barcode:", formData?.primary_barcode || formValues.primary_barcode || ""],
    ...(isTwoTube
      ? [["EDTA barcode:", formData?.secondary_barcode || formValues.secondary_barcode || ""]]
      : []),
    ["Date of sample collection:", displayCollectionDate],
    ["Time of sample collection:", enteredTime],
    ["Number of blood draw attempts:", numberOfAttempts],
  ];

   const askConfirm = (type) => ({ reason, notes: reasonNotes }) => {
    setPendingReject({
      type,
      reason,
      notes: reasonNotes,
      barcode: formData?.primary_barcode || formValues.primary_barcode || "",
    });
    setShowKitModal(false);
    closeExitModal();
  };

  const handleRejectConfirm = async ({ reason, notes: rejectNotes }) => {
    setShowKitModal(false);

    const result = await callApi(
      {
        bid: id,
        testkit: resultPost?.testkit || null,
        reject_flag: true,
        rejection_reason: reason,
        notes: rejectNotes || "",
        rejection_type: "test_kit",
        sample_collection_date: collectionDate,
        sample_collection_time: collectionTime,
        number_of_attempt: numberOfAttempts || undefined,
      },
      putUrlReject,
      "PUT",
    );
    if (!result) return;
    sessionStorage.setItem("kitRejectedId", id);
    localStorage.removeItem("secondary_barcode");
    setFormData((prev) => ({
      ...prev,
      primary_barcode: "",
      secondary_barcode: "",
      expiry_date: "",
    }));
    setSecondaryBarcode("");
    setExpiryDate("");
    setIsRejectDisabled(true);
    setUpdateKey((prev) => prev + 1);
    setKitRejected(true);
  };

  const handleExitSubmit = async ({ reason, notes: rejectNotes }) => {
    closeExitModal();

    await callApi(
      {
        bid: id,
        testkit: resultPost?.testkit || null,
        reject_flag: true,
        rejection_reason: reason,
        notes: rejectNotes || "",
        rejection_type: "appointment",
      },
      putUrlReject,
      "PUT",
    );
    localStorage.removeItem("secondary_barcode");
    localStorage.removeItem("secondary_barcode");
    navigate("/consultation-cancelled", {
      state: {
        rejectionMessage: `Consultation rejected`,
      },
    });
    auth?.showToast(
      "Rejection Successful",
      "Appointment has been rejected successfully.",
      "../assets/mhc.png",
      { icon: <XCircleFill />, className: auth?.featureFlag && "vh-toaster" },
    );
  };

  const handleConfirmAccepted = () => {
    const { type, ...rest } = pendingReject;
    setPendingReject(null);
    return type === "kit" ? handleRejectConfirm(rest) : handleExitSubmit(rest);
  };

  const barcodeFields = (() => {
    if (selectedService === "clinical") return ClinicalBarcodeFields;

    const tubeFields = resultGet?.booking_info?.vessel_info_2
      ? BarcodeTwoTubeFields
      : BarcodeOneTubeFields;

    return tubeFields.map((field) => {
      if (!field.btnGroup) return field;
      return {
        ...field,
        btnGroup: field.btnGroup.filter(
          (btn) => isRejectButtonDisplay || btn.label !== "Reject",
        ),
      };
    });
  })();

  const handlePrimaryBarcodeChange = async (event) => {
    const primaryBarcode = event.target.value.trim();
    setIsRejectDisabled(!primaryBarcode);
    if (primaryBarcode) {
      setExpiryDate("");
      setSecondaryBarcode("");
      setFormData((prevFormData) => ({
        ...prevFormData,
        primary_barcode: primaryBarcode,
        secondary_barcode: "",
        expiry_date: "",
      }));
      await callApi(
        {
          primary_barcode: primaryBarcode,
          test_type: formatTestType(resultGet.booking_info.test_type),
          study: formatStudyName("Thrive Registry"),
        }, // Request payload
        postUrlBarcode, // API URL
        "POST", // HTTP method
        {
          Authorization: `Bearer ${
            localStorage.getItem("access_token") || auth.accessToken
          }`,
          "Content-Type": "application/json", // Content-Type header
        },
      );
      auth?.allowStage("enter-barcode");
    } else {
      setExpiryDate("");
      setSecondaryBarcode("");
      setFormData((prevFormData) => ({
        ...prevFormData,
        primary_barcode: "",
        secondary_barcode: "",
        expiry_date: "",
      }));
      localStorage.removeItem("primary_barcode");
      localStorage.removeItem("secondary_barcode");
    }
  };
  useEffect(() => {
    if (
      serverError ===
      "This test kit barcode has been rejected and cannot be reused. Please assign a new test kit."
    ) {
      setIsRejectButtonDisplay(false);
    }
  }, [serverError]);
  // Handle 'Back' button click
  const handleBackClick = () => {
    navigate(-1);
  };

  const rejectionBanner = (kitRejected || resultGet?.rejection_note) && (
    <span className="reject-modal__info reject-modal__info--page">
      <img alt="" src={infoIcon} />
      <span>
        {resultGet?.rejection_note ||
          "This barcode has been rejected. Ensure new kit barcodes are used to avoid processing errors."}
      </span>
    </span>
  );

  const repeatedTest = repeatTestResult?.data?.repeated_test ?? repeatTestResult?.repeated_test;

  const repeatTestMessage =
    String(repeatedTest || "").includes("(paid)")
      ? "Repeat test. Payment not required."
      : String(repeatedTest || "").includes("(payment required)")
        ? "Repeat test. Payment required."
        : "";

  const repeatTestAlert = repeatTestMessage && (
    <div className="repeat-test-alert">
      {repeatTestMessage.toLowerCase().includes("not required") ? (
        <VHConfirmationPopup showIcon={false} warningText={repeatTestMessage} />
      ) : (
        <VHConfirmationPopup showIcon={false} infoText={repeatTestMessage} />
      )}
    </div>
  );

  const newContent = (
    <PageLayout
      heading="Sample collection"
      subHeading={
        <>
          Follow the steps below to collect the sample and enter the test kit
          and sample information
        </>
      }
      infoText={(kitRejected || resultGet?.rejection_note) && "This barcode has been rejected. Ensure new kit barcodes are used to avoid processing errors."}
      className="vh-enter-barcode-layout"
    >{selectedService === "healthCare" && (
      <ExitConsultationButton
        disabled={isRejectDisabled}
        onClick={handleExitConsultationClick}
      />
      )}
      {isLoading && <p>Loading...</p>}
      <SearchForm
        formikRef={formikRef}
        title="null"
        subtitle="null"
        className={`enter-barcode-wrapper`}
        formClass={"thrive-clinical-content"}
        colStructure={7}
        data={{
          ...formData,
          study: "Thrive Registry",
          test_type:
            userData ||
            (resultGet?.booking_info?.test_type
              ? formatTestType(resultGet.booking_info.test_type)
              : "Thrive Panel"),
          ...data,
        }}
        expiryDate={expiryDate}
        fields={fields}
        formError={"Please fill in the required fields"}
        helptext={expiryDate}
        helptextClass={"invalid-feedback-small"}
        isRejectDisabled={isRejectDisabled}
        key={updateKey}
        onBackClick={handleBackClick}
        onBlur={handlePrimaryBarcodeChange}
        onRejectClick={handleRejectKitClick}
        onSubmit={handleSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handlePrimaryBarcodeChange(e);
          }
        }}
        secondaryBarcode={secondaryBarcode}
        textAfter={
          auth?.featureFlag ? (
            "2. Prepare the patient and equipment, then collect the venous blood sample."
          ) : (
            <div className="before-wrapper">
              <span className="serial-no">2. </span>
              <span className="underline-text">
                <span className="danger-text">After</span> completing blood
                collection,
              </span>{" "}
              enter the date and time the test was performed
            </div>
          )
        }
        useLocalStorage={true}
        validationSchema={
          auth?.featureFlag ? vhClinicalBarcodeSchema : enterBarcodeSchema
        }
        lottNumber={
          expiryDate && (formData.primary_barcode || formikRef.current?.values?.primary_barcode)
            ? resultPost?.lot_number
            : ""
        }
        childrenNotes={
          <Notes
            value={notes}
            onChange={setNotes}
            disabled={false}
            label="Notes"
          />
        }
        isVhlogin={auth?.featureFlag}
      />
     <div className="outside-actions">
      
      {selectedService === "healthCare" && (
        <>
        {repeatTestAlert} 
        <button
          className="btn boots-secondary-btn-styling"
          disabled={isRejectDisabled}
          onClick={handleRejectKitClick}
        >
          Reject test kit
        </button>
        </>
      )}
      <button
        className="btn btn-rounded semi-buttons boots-primary-btn-styling"
        onClick={() => formikRef.current?.submitForm()}
      >
         Continue
      </button>
    </div>
      <RejectKitModal
        className={isTwoTube ? "reject-modal--two-barcodes" : ""}
        details={kitDetails}
        isOpen={showKitModal}
        onClose={() => setShowKitModal(false)}
        onReject={askConfirm("kit")}
        reasons={vhKitRejectionReasons}
      />
      <ExitModal
        bookingId={id}
        className="exit-consultation-modal--pp"
        confirmLabel="Confirm & reject"
        isOpen={showExitModal}
        notes={exitNotes}
        notesAlwaysRequired
        onClose={closeExitModal}
        onNotesChange={setExitNotes}
        onSubmit={askConfirm("exit")}
        reasonLabel="Reason"
        reasons={vhAppointmentRejectionReasons}
        showCancel={false}
        showRequiredMark={false}
      />
      <ConfirmModal
        isOpen={Boolean(pendingReject)}
        onClose={() => setPendingReject(null)}
        onConfirm={handleConfirmAccepted}
        title={confirmContent[pendingReject?.type]?.title || ""}
      >
        <p>{confirmContent[pendingReject?.type]?.text}</p>
        {pendingReject?.type === "kit" && (
          <p className="boots-bold">{pendingReject.barcode || "-"}</p>
        )}
      </ConfirmModal>
    </PageLayout>
  );

  return (
    <LoggedInLayout
      buttonLabel={"Logout"}
      img={img}
      isActivation={false}
      links={links}
      logo={logo}
      pageTitle={auth.title}
      userInitials={auth.userInitials}
      isVhlogin={auth?.featureFlag}
      vitalityLayout={auth?.featureFlag}
    >
      {auth?.featureFlag ? (
        newContent
      ) : (
        <>
          {isLoading && <p>Loading...</p>}
          {resultGet?.rejection_note ? (
            <Alert variant={"danger"}>{resultGet?.rejection_note}</Alert>
          ) : (
            <></>
          )}
          <SearchForm
            formikRef={formikRef}
            title={`${selectedService === "healthCare" ? "Sample collection" : "Participant record"}`}
            subtitle={`${selectedService === "healthCare" ? "Follow the steps below to collect the sample and enter the test kit and sample information" : "Please confirm details to continue registration"}`}
            className={`enter-barcode-wrapper`}
            formClass={"thrive-clinical-content"}
            colStructure={7}
            data={{ ...formData, test_type: userData, ...data }}
            expiryDate={expiryDate}
            fields={fields}
            formError={"Please fill in the required fields"}
            helptext={expiryDate}
            helptextClass={"invalid-feedback-small"}
            isRejectDisabled={isRejectDisabled}
            key={updateKey}
            onBackClick={handleBackClick}
            onBlur={handlePrimaryBarcodeChange}
            onRejectClick={handleRejectClick}
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handlePrimaryBarcodeChange(e);
              }
            }}
            secondaryBarcode={secondaryBarcode}
            textAfter={
              <div className="before-wrapper">
                <span className="serial-no">2. </span>
                <span className="underline-text">
                  <span className="danger-text">After</span> completing blood
                  collection,
                </span>{" "}
                enter the date and time the test was performed
              </div>
            }
            useLocalStorage={true}
            validationSchema={enterBarcodeSchema}
            lottNumber={resultPost?.lot_number}
          />
          <div className="outside-actions">
            <button
              className="btn btn-outline btn-back semi-buttons"
              onClick={handleBackClick}
            >
              Back
            </button>
            {selectedService === "healthCare" && (
              <button
                className="btn btn-green"
                disabled={isRejectDisabled}
                onClick={handleRejectClick}
              >
                Reject
              </button>
            )}
            <button
              className="btn btn-primary btn-rounded semi-buttons"
              onClick={() => formikRef.current?.submitForm()}
            >
              Save & Continue
            </button>
          </div>
        </>
      )}
    </LoggedInLayout>
  );
}

export default EnterBarcode;